import {
  ChannelDetails,
  ChannelTypeEnum,
  dispatch,
  getClosedIssueThreads,
  getCustomerThreads,
  getGroupThreads,
  getIssueThreads,
  store,
  upsertChannel,
} from '@zonofi/seller-rtk-store';
import { ChannelCustomType } from '../types';
import {
  FiltersPayload,
  useGetThreadsListFromApi,
} from './get-threads-list-from-api';
import { useCallback, useRef } from 'react';
import { isEmpty } from 'lodash';
import { sortThreads } from '../functions';
import { isWeb } from '@zonofi/common';

interface ThreadsListPayload {
  type: ChannelCustomType;
}

export interface ThreadsPayload extends ThreadsListPayload {
  filters?: FiltersPayload;
  isClosedIssues?: boolean;
  refresh?: boolean;
  nextPageToken?: string;
}

type NextPageToken = Record<ChannelCustomType, string | undefined>;
type ChannelDataEntry = Record<string, Partial<ChannelDetails>>;

const ThreadTabTypeMap: Partial<Record<ChannelCustomType, string | string[]>> =
  {
    [ChannelCustomType.CustomerThread]: isWeb() ? ['C', 'CUST'] : ['C'],
    [ChannelCustomType.IssueThread]: [ChannelTypeEnum.ISSUETHREAD],
    [ChannelCustomType.GroupThread]: ['INT', 'WAHA', 'WAGR'],
  };

const selectorMap = {
  [ChannelCustomType.CustomerThread]: getCustomerThreads,
  [ChannelCustomType.IssueThread]: getIssueThreads,
  [ChannelCustomType.GroupThread]: getGroupThreads,
  closedissues: getClosedIssueThreads,
};

export const useGetThreads = () => {
  const { fetchThreadsData, processThreadData } = useGetThreadsListFromApi();

  // Pagination + temporary data refs
  const nextPageTokenRef = useRef<NextPageToken>(
    Object.fromEntries(
      Object.values(ChannelCustomType).map((x) => [x, undefined])
    ) as NextPageToken
  );
  const channelDataRef = useRef<ChannelDataEntry>({});

  /** Helper: check if any filter applied */
  const hasAppliedFilters = useCallback((filters?: FiltersPayload) => {
    if (isEmpty(filters)) return false;
    const { status, ...restFilters } = filters || {};
    const statusFiltersApplied =
      Object.values(status || {}).flatMap((x) => x).length !== 0;
    const restFiltersApplied =
      Object.values(restFilters || {}).flatMap((x) => x).length !== 0;
    return statusFiltersApplied || restFiltersApplied;
  }, []);

  /** Process & store metadata for unread count tracking */
  const processChannelItem = useCallback((channel: Partial<ChannelDetails>) => {
    const channelId = channel.id;
    if (!channelId) return;
    channelDataRef.current[channelId] = channel;
  }, []);

  /** --- Main Paginated Fetch --- */
  const fetchThreads = useCallback(
    async ({
      type,
      filters,
      isClosedIssues = false,
      refresh = false,
      nextPageToken,
    }: ThreadsPayload): Promise<{
      data: ChannelDetails[];
      hasMore: boolean;
      nextPageToken: string | undefined;
    }> => {
      try {
        // Reset pagination if requested
        if (refresh) nextPageTokenRef.current[type] = undefined;

        const currentToken = nextPageToken ?? nextPageTokenRef.current[type];
        const filterApplied = hasAppliedFilters(filters);
        const selectorFn =
          type === ChannelCustomType.IssueThread && isClosedIssues
            ? selectorMap['closedissues']
            : selectorMap[type as keyof typeof selectorMap];

        let responseData: ChannelDetails[] = [];
        let newNextPageToken: string | undefined;
        let response;

        // Case 1: Filtered threads → API only
        if (filterApplied) {
          response = await fetchThreadsData({
            channelType: ThreadTabTypeMap[type] || 'C',
            filters,
            nextPageToken: currentToken,
          });
        }
        // Case 2: Unfiltered threads → API
        else {
          response = await fetchThreadsData({
            channelType: ThreadTabTypeMap[type] || 'C',
            nextPageToken: currentToken,
          });
        }

        if (response?.data?.length) {
          const processed = response.data
            .map((item) => processThreadData(item, type))
            .filter(Boolean) as ChannelDetails[];
          processed.forEach(processChannelItem);
          responseData = processed;
          newNextPageToken = response.nextPage;
        }

        // Update Redux Store
        if (Object.keys(channelDataRef.current).length) {
          dispatch(upsertChannel(channelDataRef.current));
          channelDataRef.current = {};
        }

        // Persist token for future calls
        nextPageTokenRef.current[type] = newNextPageToken;

        // Prepare final list (sorted)
        let finalThreads: ChannelDetails[];

        if (filterApplied) {
          finalThreads = sortThreads(responseData);
        } else {
          const storeData =
            (selectorFn?.(store.getState()) as ChannelDetails[]) ?? [];
          finalThreads = sortThreads(storeData);
        }
        return {
          data: finalThreads,
          hasMore:
            newNextPageToken !== undefined && newNextPageToken !== 'stop',
          nextPageToken: newNextPageToken,
        };
      } catch (error) {
        console.error('fetchThreads error:', error);
        return { data: [], hasMore: false, nextPageToken: undefined };
      }
    },
    [fetchThreadsData, processThreadData, processChannelItem, hasAppliedFilters]
  );

  return {
    fetchThreads,
    threadsSelectorMap: selectorMap,
    getNextPageToken: (type: ChannelCustomType) =>
      nextPageTokenRef.current[type],
  };
};
